import { useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

const TOKEN_EXPIRY_BUFFER_SECONDS = 60;

function isTokenExpiringSoon(expiresAt: number | undefined): boolean {
  if (!expiresAt) return true;
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return expiresAt - nowInSeconds <= TOKEN_EXPIRY_BUFFER_SECONDS;
}

export default function useIdentity() {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  const refreshToken = async (): Promise<string> => {
    try {
      const user = await authRef.current.signinSilent();
      if (!user) {
        throw new Error('Failed to get user after silent sign-in');
      }
      return user.access_token;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    } finally {
      refreshPromiseRef.current = null;
    }
  };

  const getAccessToken = useCallback(async (forceRefresh: boolean = false): Promise<string> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const currentAuth = authRef.current;
    if (
      !forceRefresh &&
      currentAuth.user &&
      !currentAuth.user.expired &&
      !isTokenExpiringSoon(currentAuth.user.expires_at)
    ) {
      return currentAuth.user.access_token;
    }

    refreshPromiseRef.current = refreshToken();
    return refreshPromiseRef.current;
  }, []);

  return {
    signin: auth.signinSilent,
    loading: auth.isLoading,
    error: auth.error,
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    getAccessToken,
  };
}
